<!-- eslint-disable -->
<template>
  <div class="leads-crm">
    <!-- Header -->
    <header class="lead-header shadow-sm">
      <div class="lead-header__content">
        <div class="d-flex align-items-center">
          <div class="lead-header__icon mr-2">
            <feather-icon icon="UserPlusIcon" size="26" />
          </div>
          <div>
            <h1 class="lead-header__title">Leads &amp; Re-engagement</h1>
            <p class="lead-header__subtitle">
              Check the list, assign to a salesperson — he works it from his pipeline and the status reflects back here.
            </p>
          </div>
        </div>
        <div class="lead-header__actions mt-2 mt-lg-0">
          <!--
            Karim, 4 Sep: "excel template for the sales people to add all the
            current data of the current leads... so they upload each one
            current work and continue on crm." Download the blank template,
            fill it in (Status column decides whether a row also opens an
            opportunity — Won included), upload it back.
          -->
          <button
            type="button"
            class="lead-btn lead-btn--ghost"
            :disabled="downloadingTemplate"
            @click="downloadLeadTemplate"
          >
            <feather-icon icon="DownloadIcon" size="16" />
            <span>{{ downloadingTemplate ? 'Preparing…' : 'Download Template' }}</span>
          </button>
          <input
            ref="importFileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="lead-file-input"
            @change="onImportFileChosen"
          >
          <button
            type="button"
            class="lead-btn lead-btn--ghost"
            :disabled="importing"
            @click="$refs.importFileInput.click()"
          >
            <feather-icon :icon="importing ? 'LoaderIcon' : 'UploadIcon'" size="16" />
            <span>{{ importing ? 'Importing…' : 'Upload Filled Sheet' }}</span>
          </button>
          <button type="button" class="lead-btn lead-btn--primary" @click="openAdd()">
            <feather-icon icon="PlusIcon" size="16" />
            <span>Add Lead</span>
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <nav class="lead-tabs">
        <button
          type="button"
          class="lead-tab"
          :class="{ 'lead-tab--active': tab === 'leads' }"
          @click="tab = 'leads'"
        >
          <feather-icon icon="TrendingUpIcon" size="16" />
          <span>Leads</span>
          <span class="lead-tab__badge">{{ openLeadCount }}</span>
        </button>
        <button
          type="button"
          class="lead-tab"
          :class="{ 'lead-tab--active': tab === 'customers' }"
          @click="tab = 'customers'; if (!customers.length) fetchCustomers()"
        >
          <feather-icon icon="RotateCcwIcon" size="16" />
          <span>Dormant Customers</span>
          <span class="lead-tab__badge">{{ customerCount }}</span>
        </button>
      </nav>
    </header>

    <!-- ============ LEADS TAB ============ -->
    <template v-if="tab === 'leads'">
      <!-- Stats -->
      <section class="lead-stats">
        <div v-for="s in stats" :key="s.key" class="lead-stat" :class="`lead-stat--${s.variant}`">
          <div class="lead-stat__icon"><feather-icon :icon="s.icon" size="18" /></div>
          <div>
            <div class="lead-stat__value">{{ s.value }}</div>
            <div class="lead-stat__label">{{ s.label }}</div>
          </div>
        </div>
      </section>

      <!-- Toolbar -->
      <section class="lead-toolbar">
        <div class="lead-search">
          <feather-icon icon="SearchIcon" size="16" class="lead-search__icon" />
          <input v-model="searchQuery" type="text" class="lead-search__input" placeholder="Search company, contact, email or CR number…" />
        </div>
        <select v-model="statusFilter" class="lead-select ml-2" @change="fetchLeads">
          <option value="open">Open leads</option>
          <option value="closed">Closed (won / lost)</option>
          <option value="">All</option>
        </select>
        <select v-if="access.canViewTeam" v-model="assignedFilter" class="lead-select ml-2" @change="fetchLeads">
          <option value="">Anyone</option>
          <option value="no">Unassigned</option>
          <option value="yes">Assigned</option>
        </select>
        <select v-if="access.canViewTeam" v-model="ownerFilter" class="lead-select ml-2">
          <option :value="null">All salespeople</option>
          <option v-for="u in team" :key="u.id" :value="u.name">{{ memberLabel(u) }}</option>
        </select>
        <button type="button" class="lead-btn lead-btn--ghost ml-2" @click="fetchLeads">
          <feather-icon icon="RefreshCwIcon" size="15" /> <span>Refresh</span>
        </button>
      </section>

      <section v-if="access.canViewTeam && selectionCount > 0" class="lead-bulk-bar">
        <span class="lead-bulk-bar__count">{{ selectionCount }} selected</span>
        <select v-model="bulkAssignTo" class="lead-select ml-2">
          <option value="">Assign to…</option>
          <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
        </select>
        <button type="button" class="lead-btn lead-btn--primary ml-2" :disabled="bulkBusy || !bulkAssignTo" @click="bulkAssign">
          <feather-icon icon="UserCheckIcon" size="15" /> <span>Assign</span>
        </button>
        <button type="button" class="lead-btn lead-btn--danger ml-2" :disabled="bulkBusy" @click="bulkDelete">
          <feather-icon icon="Trash2Icon" size="15" /> <span>Delete</span>
        </button>
        <button type="button" class="lead-btn lead-btn--ghost ml-2" @click="clearSelection">
          <span>Cancel</span>
        </button>
      </section>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading…</span></div>
        <p class="mt-2 text-muted">Loading leads…</p>
      </div>

      <div v-else-if="!filteredLeads.length" class="lead-empty">
        <feather-icon icon="InboxIcon" size="42" class="text-muted mb-1" />
        <h4>No leads here</h4>
        <p class="text-muted">Add a lead, or change the filters above.</p>
      </div>

      <!-- Leads table -->
      <section v-else class="lead-table-wrap">
        <table class="lead-table">
          <thead>
            <tr>
              <th v-if="access.canViewTeam">
                <input type="checkbox" :checked="allFilteredSelected" title="Select all filtered" @change="toggleSelectAll" />
              </th>
              <th>Company</th>
              <th>Contact</th>
              <th>Scope / service</th>
              <th>Source</th>
              <th>Status</th>
              <th>Assigned to</th>
              <th>Next activity</th>
              <th>Done so far</th>
              <th class="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lead in filteredLeads" :key="lead.id" :class="rowClass(lead)">
              <td v-if="access.canViewTeam">
                <input type="checkbox" :checked="isSelected(lead)" title="Select" @change="toggleSelect(lead)" />
              </td>
              <td>
                <div class="font-weight-bold lead-trunc lead-trunc--company" :title="lead.title">{{ lead.title }}</div>
                <small class="text-muted">{{ lead.leadCode || ('LEAD-' + lead.leadId) }}</small>
                <!-- The CR number identifies the facility (BRD §1), so show it
                     where the facility is named; it is also searchable below. -->
                <small v-if="lead.crNumber" class="text-muted d-block">CR {{ lead.crNumber }}</small>
                <small v-if="lead.isBroker" class="lead-chip">Broker</small>
              </td>
              <!-- Karim, 27 Sep: contact person merged into contact details to save width. -->
              <td class="lead-contact">
                <div v-if="lead.client" class="lead-trunc lead-trunc--email font-weight-bold" :title="lead.client">{{ lead.client }}</div>
                <a v-if="lead.email" :href="`mailto:${lead.email}`" class="lead-trunc lead-trunc--email" :title="lead.email" @click.stop>
                  <feather-icon icon="MailIcon" size="11" class="mr-05" />{{ lead.email }}
                </a>
                <a v-if="lead.phone" :href="`tel:${lead.phone}`" class="lead-trunc lead-trunc--email" :title="lead.phone" @click.stop>
                  <feather-icon icon="PhoneIcon" size="11" class="mr-05" />{{ lead.phone }}
                </a>
                <span v-if="!lead.client && !lead.email && !lead.phone" class="text-muted">—</span>
              </td>
              <td class="lead-scope-cell" :title="lead.scope">{{ lead.scope || '—' }}</td>
              <td><span v-if="lead.source" class="lead-chip">{{ lead.source }}</span><span v-else>—</span></td>

              <!-- Status reflects the salesperson's work on the opportunity -->
              <td>
                <span class="lead-status" :style="statusStyle(lead.stage)">{{ stageName(lead.stage) }}</span>
                <div v-if="isClosed(lead)" class="lead-outcome" :class="lead.stage === 'won' ? 'lead-outcome--won' : 'lead-outcome--lost'">
                  {{ lead.stage === 'won' ? 'Now a customer' : 'Lost' }}
                </div>
              </td>

              <!-- Assignment -->
              <td>
                <select
                  v-if="access.canViewTeam"
                  class="lead-select lead-select--sm"
                  :value="lead.ownerId || ''"
                  @click.stop
                  @change="assignLead(lead, $event.target.value)"
                >
                  <option value="">— unassigned —</option>
                  <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
                </select>
                <span v-else>{{ lead.owner || '—' }}</span>
              </td>

              <td>
                <span v-if="lead.nextActivity" class="lead-nowrap" :class="{ 'text-danger font-weight-bold': isOverdue(lead.nextActivity.dueDate) }">
                  {{ lead.nextActivity.type }} · {{ lead.nextActivity.dueDate }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>

              <!--
                WHAT HAS ALREADY BEEN DONE — Karim, 2 Sep.
                "Next activity" alone cannot tell a fresh lead from a dying one: a
                lead called four times and a lead nobody has touched both read
                "Call · Tuesday". This is the brief — how many, and the last one.
                Hover for the recent history.
              -->
              <td class="lead-done">
                <template v-if="lead.doneActivities && lead.doneActivities.count">
                  <span class="lead-done__count lead-nowrap" :title="doneTooltip(lead)">
                    {{ lead.doneActivities.count }}
                    {{ lead.doneActivities.count === 1 ? 'activity' : 'activities' }}
                  </span>
                  <small v-if="lead.doneActivities.last" class="lead-done__last lead-trunc lead-trunc--note">
                    last: {{ lead.doneActivities.last.type }}
                    · {{ (lead.doneActivities.last.date || '').slice(0, 10) }}
                  </small>
                  <small
                    v-if="lead.doneActivities.last && lead.doneActivities.last.note"
                    class="lead-done__note lead-trunc lead-trunc--note"
                    :title="lead.doneActivities.last.note"
                  >{{ lead.doneActivities.last.note }}</small>
                </template>
                <span v-else class="text-muted" title="Nobody has worked this lead yet">
                  Not contacted
                </span>
              </td>

              <td class="text-right lead-nowrap">
                <!-- Karim, 8 Sep: "the button Send to pipeline is not needed
                     because once we assign to a salesperson, it automatically
                     creates a new opportunity." Assign now converts on its own. -->
                <button
                  type="button"
                  class="lead-btn lead-btn--ghost lead-btn--sm ml-1"
                  title="Log a call/email/visit and schedule the next one"
                  @click.stop="openActivityModal(lead)"
                >
                  <feather-icon icon="ClockIcon" size="13" />
                </button>
                <button
                  type="button"
                  class="lead-btn lead-btn--ghost lead-btn--sm ml-1"
                  title="Send an email (via your Gmail)"
                  @click.stop="openActivityModal(lead, true)"
                >
                  <feather-icon icon="MailIcon" size="13" />
                </button>
                <button type="button" class="lead-btn lead-btn--ghost lead-btn--sm ml-1" @click.stop="editLead(lead)">
                  <feather-icon icon="EditIcon" size="13" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>

    <!-- ============ DORMANT CUSTOMERS TAB ============ -->
    <template v-else>
      <section class="lead-toolbar">
        <div class="lead-search">
          <feather-icon icon="SearchIcon" size="16" class="lead-search__icon" />
          <input v-model="custSearch" type="text" class="lead-search__input" placeholder="Search customer or email…" />
        </div>
        <select v-model.number="months" class="lead-select ml-2" @change="fetchCustomers">
          <option :value="6">No certificate for 6+ months</option>
          <option :value="12">No certificate for 12+ months</option>
          <option :value="24">No certificate for 24+ months</option>
        </select>
        <button type="button" class="lead-btn lead-btn--ghost ml-2" @click="fetchCustomers">
          <feather-icon icon="RefreshCwIcon" size="15" /> <span>Refresh</span>
        </button>
      </section>

      <div v-if="loadingCustomers" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading…</span></div>
        <p class="mt-2 text-muted">Finding customers who went quiet…</p>
      </div>

      <div v-else-if="!filteredCustomers.length" class="lead-empty">
        <feather-icon icon="CheckCircleIcon" size="42" class="text-success mb-1" />
        <h4>Nothing to re-engage</h4>
        <p class="text-muted">Every customer has been active within this period.</p>
      </div>

      <section v-else class="lead-table-wrap">
        <table class="lead-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Company</th>
              <th>Contact details</th>
              <th class="text-center">Certificates</th>
              <th>Last certificate</th>
              <th>Assign to</th>
              <th class="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in filteredCustomers" :key="c.clientId">
              <td class="font-weight-bold">{{ c.name || '—' }}</td>
              <td>{{ c.company || '—' }}</td>
              <td class="lead-contact">
                <a v-if="c.email" :href="`mailto:${c.email}`"><feather-icon icon="MailIcon" size="11" class="mr-05" />{{ c.email }}</a>
                <a v-if="c.phone" :href="`tel:${c.phone}`"><feather-icon icon="PhoneIcon" size="11" class="mr-05" />{{ c.phone }}</a>
                <span v-if="!c.email && !c.phone" class="text-muted">—</span>
              </td>
              <td class="text-center">{{ c.certificates }}</td>
              <td>
                <span :class="c.monthsSince >= 24 ? 'text-danger font-weight-bold' : ''">
                  {{ c.lastCertificate || 'unknown' }}
                  <small v-if="c.monthsSince" class="text-muted">({{ c.monthsSince }} mo)</small>
                </span>
              </td>
              <td>
                <select class="lead-select lead-select--sm" v-model="custAssign[c.clientId]">
                  <option :value="undefined">— select —</option>
                  <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
                </select>
              </td>
              <td class="text-right">
                <button
                  type="button"
                  class="lead-btn lead-btn--success lead-btn--sm"
                  :disabled="!custAssign[c.clientId]"
                  title="Create a re-engagement opportunity in that salesperson's pipeline"
                  @click="assignCustomer(c)"
                >
                  <feather-icon icon="SendIcon" size="13" /> <span>Assign &amp; call</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>

    <!-- ADD / EDIT LEAD MODAL -->
    <div v-if="showModal" class="lead-modal" @click.self="showModal = false">
      <div class="lead-modal__box">
        <header class="lead-modal__head">
          <h4>{{ form.id ? 'Edit Lead' : 'New Lead' }}</h4>
          <button type="button" class="lead-modal__close" @click="showModal = false">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </header>

        <div class="lead-modal__body">
          <div class="form-row">
            <div class="form-group col-md-6">
              <label>Company name *</label>
              <input v-model="form.company_name" type="text" class="form-control" placeholder="Facility / company" />
            </div>
            <div class="form-group col-md-6">
              <label>Contact person</label>
              <input v-model="form.contact_person" type="text" class="form-control" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label>Email</label>
              <input v-model="form.email" type="email" class="form-control" />
            </div>
            <div class="form-group col-md-6">
              <label>Contact number</label>
              <input v-model="form.contact_number" type="text" class="form-control" />
            </div>
          </div>

          <!--
            BRD §1 + US 2.3: the CR (Commercial Registration) number is the
            facility's identity. Captured here so the deal inherits it on
            conversion — BRD §3.2 makes it a required Deal field and §3.1 will not
            let a lead reach Qualified without it. Optional on the lead itself
            (§2.2's required list does not include it) because a first phone call
            rarely produces it. Duplicate detection is separate, unbuilt work (G-2).
          -->
          <div class="form-row">
            <div class="form-group col-md-6">
              <label>CR number</label>
              <input v-model="form.cr_number" type="text" class="form-control" placeholder="Commercial registration no." />
              <small v-if="crCheck.loading" class="text-muted d-block mt-1">Checking…</small>
              <small v-else-if="crCheck.warning" class="text-danger d-block mt-1">
                {{ crCheck.warning }}
                <a href="#" @click.prevent="openMatchedFacility">Open it</a> instead of creating a duplicate.
              </small>
              <small v-else-if="form.erp_facility_id" class="text-success d-block mt-1">
                Linked to the existing facility (ERP #{{ form.erp_facility_id }}) — the opportunity will inherit it.
              </small>
              <small v-else class="text-muted d-block mt-1">Identifies the facility. Needed before the deal can be qualified.</small>
            </div>
            <!--
              BRD §2.2 lists "Is Broker? (Boolean)" as REQUIRED: the party we talk
              to may be a broker acting for several facilities (§1.1), which
              changes who the quotation is addressed to (§4.2 Broker Name).
              No default — an unanswered question must not read as "no".
            -->
            <div class="form-group col-md-6">
              <label>Is this contact a broker? *</label>
              <select v-model="form.is_broker" class="form-control">
                <option :value="null">— select —</option>
                <option :value="false">No — the facility itself</option>
                <option :value="true">Yes — a broker / agent</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label>Scope / service</label>
              <select v-model="form.scope" class="form-control" :disabled="!schemeOptions.length">
                <option :value="null">
                  {{ schemeOptions.length ? '— select —' : 'No products loaded' }}
                </option>
                <option v-for="s in schemeOptions" :key="s" :value="s">{{ s }}</option>
              </select>
              <!-- Never leave the rep guessing why a dropdown is empty. -->
              <small v-if="schemeError" class="text-danger d-block mt-1">{{ schemeError }}</small>
              <small v-else-if="schemeOptions.length" class="text-muted d-block mt-1">
                {{ schemeOptions.length }} products available
              </small>
            </div>
            <!--
              BRD §2.2: Source is REQUIRED. It is what the §7.1 "Lead Sources" and
              "AVG Certificate based on source" dashboards are built from — a lead
              with no source is invisible to every marketing metric.
            -->
            <div class="form-group col-md-6">
              <label>Source *</label>
              <select v-model="form.source" class="form-control">
                <option :value="null">— select —</option>
                <option v-for="s in sources" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <!--
              BRD §2.2: Campaign is REQUIRED, and it is the finer half of the pair
              — Source says the channel, Campaign says which spend produced the
              lead, which is what the Marketing CPL / CAC / ROI figures (§7.1) need.
              Karim, 18 Sep: real campaigns now exist (crm/campaigns) — a searchable
              picker replaces the free-text box, but still accepts typing a name
              that doesn't match anything (onCampaignPicked leaves campaign_id null
              in that case; the plain text still saves, same as before this change).
            -->
            <div class="form-group col-md-6">
              <label>Campaign *</label>
              <v-select
                :value="form.campaign_id ? { id: form.campaign_id, displayName: form.campaign } : (form.campaign ? { name: form.campaign, displayName: form.campaign } : null)"
                :options="campaignOptions"
                label="displayName"
                taggable
                placeholder="Search or type a campaign…"
                @input="onCampaignPicked"
              />
              <small class="text-muted">Pick an existing campaign, or type a new name to add it later from Campaign Management.</small>
            </div>
            <!-- BRD §2.2 optional field; also feeds the §7.2 level-4 category filter. -->
            <div class="form-group col-md-6">
              <label>Industry</label>
              <input v-model="form.industry" type="text" class="form-control" placeholder="e.g. Food, Cosmetics, Electrical" />
            </div>
          </div>

          <!-- BRD §2.2 optional fields — the two the team fills off a business card. -->
          <div class="form-row">
            <div class="form-group col-md-6">
              <label>Job position</label>
              <input v-model="form.job_position" type="text" class="form-control" placeholder="e.g. QA Manager" />
            </div>
            <div class="form-group col-md-6">
              <label>Website</label>
              <input v-model="form.website" type="text" class="form-control" placeholder="company.com" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label>Assign to salesperson</label>
              <select v-model="form.responsible_id" class="form-control">
                <option :value="null">— unassigned —</option>
                <option v-for="u in team" :key="u.id" :value="u.id">{{ memberLabel(u) }}</option>
              </select>
            </div>
            <div class="form-group col-md-6">
              <label>Priority</label>
              <select v-model="form.priority" class="form-control">
                <option>High</option><option>Medium</option><option>Low</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>Address</label>
            <textarea v-model="form.address" rows="2" class="form-control"></textarea>
          </div>
        </div>

        <footer class="lead-modal__foot">
          <!--
            The message says WHY the field is required, not just that it is —
            BRD §2.2 marks Source, Campaign and "Is Broker?" required because the
            marketing dashboards (§7.1) and the broker model (§1.1) are useless
            without them. Enforced here, in the form, and NOT in the API: the same
            endpoint serves the website/email intake and the CSV import (§8.3),
            which cannot guarantee these values, and rejecting those imports would
            cause exactly the lead leakage this CRM exists to stop.
          -->
          <small v-if="missingRequired" class="text-danger mr-auto">{{ missingRequired }}</small>
          <button type="button" class="lead-btn lead-btn--secondary" @click="showModal = false">Cancel</button>
          <button type="button" class="lead-btn lead-btn--primary" :disabled="!!missingRequired || saving" @click="saveLead">
            <feather-icon icon="SaveIcon" size="15" />
            <span>{{ saving ? 'Saving…' : 'Save Lead' }}</span>
          </button>
        </footer>
      </div>
    </div>

    <!--
      Import results — Karim, 4 Sep: bulk "current work" upload. Shown after
      every upload so a rep can see exactly which rows became a lead, which
      also opened an opportunity, and which rows failed and why — rather than
      trusting a single toast for a multi-row upload.
    -->
    <div
      v-if="importResults"
      class="lead-modal"
      @click.self="closeImportResults"
    >
      <div class="lead-modal__box">
        <header class="lead-modal__head">
          <h4>Import results</h4>
          <button type="button" class="lead-modal__close" @click="closeImportResults">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </header>
        <div class="lead-modal__body">
          <p class="mb-2">
            <strong>{{ importResults.created || 0 }}</strong> lead{{ importResults.created === 1 ? '' : 's' }} added.
            Open an opportunity for any of them from the lead's own page when you're ready.
          </p>
          <table
            v-if="importResults.rows && importResults.rows.length"
            class="lead-import-table"
          >
            <thead>
              <tr>
                <th>Row</th>
                <th>Company</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in importResults.rows"
                :key="r.row"
                :class="{ 'lead-import-table__row--error': r.status === 'error' }"
              >
                <td>{{ r.row }}</td>
                <td>{{ r.company || '—' }}</td>
                <td>
                  <span v-if="r.status === 'error'">{{ r.message }}</span>
                  <span v-else>
                    {{ r.leadCode }} — {{ r.stage }}
                    <template v-if="r.opportunityCreated">, opportunity opened</template>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="lead-modal__foot">
          <button type="button" class="lead-btn lead-btn--primary" @click="closeImportResults">Done</button>
        </footer>
      </div>
    </div>

    <!-- Karim, 7 Sep: "when the client is new I cannot create activity." -->
    <activity-modal
      :visible="showActivityModal"
      :card="activityLead"
      :start-in-email-mode="startInEmailMode"
      @close="closeActivityModal"
      @submit="submitLeadActivity"
      @send-email="submitLeadEmail"
    />
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// Karim, 7 Sep: "when the client is new I cannot create activity." The backend
// endpoint (crm/leads/{id}/activity) already existed — this page just never had
// a button that opened anything to call it with. Reusing Pipeline's own modal
// rather than building a second one.
import ActivityModal from '../pipeline/components/ActivityModal.vue'
import vSelect from 'vue-select'

const DEFAULT_STAGES = [
  { id: 'new', name: 'New', color: '#2563eb' },
  { id: 'proposal', name: 'Proposal Sent', color: '#3b82f6' },
  { id: 'responded', name: 'Responded', color: '#2563eb' },
  { id: 'negotiation', name: 'Negotiation', color: '#ec4899' },
  { id: 'quotation', name: 'Quotation', color: '#a855f7' },
  { id: 'won', name: 'Won', color: '#10b981' },
  { id: 'rejected', name: 'Rejected', color: '#ef4444' }
]

export default {
  name: 'LeadsCRM',
  components: {
    ActivityModal,
    vSelect,
  },
  data() {
    return {
      tab: 'leads',
      showActivityModal: false,
      startInEmailMode: false,
      activityLead: null,
      loading: false,
      loadingCustomers: false,
      saving: false,
      searchQuery: '',
      highlightId: null,
      statusFilter: 'open',
      assignedFilter: '',
      ownerFilter: null,
      leads: [],
      stages: DEFAULT_STAGES,
      team: [],
      access: { scope: 'own', canViewTeam: false, seesAll: false },
      schemeOptions: [],
      schemeError: null,
      sources: ['Referral', 'Online', 'Scouting', 'Consultant', 'Exhibition', 'Existing Client', 'Other'],
      // Karim, 18 Sep: real campaigns, selectable — crm/campaigns?activeOnly=1.
      campaignOptions: [],
      // Karim, 4 Sep: bulk "current work" import — see downloadLeadTemplate()/onImportFileChosen().
      downloadingTemplate: false,
      importing: false,
      importResults: null,
      // dormant customers
      customers: [],
      customerCount: 0,
      custSearch: '',
      custAssign: {},
      months: 12,
      // modal
      showModal: false,
      form: this.emptyForm(),
      // CR duplicate check (BRD US 2.3) — mirrors CompaniesCRM's checkCr, but
      // here it also LINKS the lead to the matched facility (erp_facility_id)
      // instead of only warning, so the opportunity it converts into actually
      // carries company_id and the company becomes visible to the assigned rep.
      crCheck: { loading: false, warning: null, facilityId: null },
      crCheckTimer: null,
      // multi-select + bulk actions (Karim, 2 Sep: "select all filtered... assign, delete")
      selectedIds: [],
      bulkAssignTo: '',
      bulkBusy: false
    }
  },
  computed: {
    allFilteredSelected() {
      return this.filteredLeads.length > 0 && this.filteredLeads.every(l => this.selectedIds.includes(l.leadId))
    },
    selectionCount() {
      return this.selectedIds.length
    },
    filteredLeads() {
      let r = [...this.leads]
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        r = r.filter(l =>
          (l.title && l.title.toLowerCase().includes(q)) ||
          (l.client && l.client.toLowerCase().includes(q)) ||
          (l.email && l.email.toLowerCase().includes(q)) ||
          // BRD US 6.2: find a record by CR Number. Client-side only — the
          // server-side `search` param still covers company/contact/email.
          (l.crNumber && String(l.crNumber).toLowerCase().includes(q))
        )
      }
      if (this.ownerFilter) r = r.filter(l => l.owner === this.ownerFilter)
      return r
    },
    filteredCustomers() {
      if (!this.custSearch) return this.customers
      const q = this.custSearch.toLowerCase()
      return this.customers.filter(c =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.company && c.company.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q))
      )
    },
    openLeadCount() {
      return this.leads.filter(l => !this.isClosed(l)).length
    },
    /**
     * The BRD §2.2 required set, in the order a rep fills the form. Returns the
     * reason the lead cannot be saved yet, or '' when it can.
     * Company name is the only one the API also enforces (validate: required).
     */
    missingRequired() {
      const f = this.form
      if (!f.company_name || !String(f.company_name).trim()) return 'A company / facility name is required.'
      if (!f.source) return 'Source is required — the marketing dashboards report leads by source.'
      if (!f.campaign || !String(f.campaign).trim()) return 'Campaign is required — it is how cost per lead and ROI are attributed.'
      if (f.is_broker === null || f.is_broker === undefined || f.is_broker === '') {
        return 'Say whether this contact is a broker — a broker may act for several facilities, and the quotation is addressed to them, not the factory.'
      }
      return ''
    },
    stats() {
      const unassigned = this.leads.filter(l => !l.ownerId && !this.isClosed(l)).length
      const won = this.leads.filter(l => l.stage === 'won').length
      const overdue = this.leads.filter(l => l.nextActivity && this.isOverdue(l.nextActivity.dueDate)).length
      return [
        { key: 'total', label: 'Leads shown', value: this.leads.length, icon: 'UsersIcon', variant: 'primary' },
        { key: 'unassigned', label: 'Unassigned', value: unassigned, icon: 'UserXIcon', variant: 'primary' },
        { key: 'won', label: 'Won (now customers)', value: won, icon: 'CheckCircleIcon', variant: 'success' },
        { key: 'overdue', label: 'Overdue activity', value: overdue, icon: 'AlertCircleIcon', variant: 'danger' }
      ]
    }
  },
  watch: {
    'form.cr_number'(value) {
      clearTimeout(this.crCheckTimer)
      const cr = String(value || '').trim()
      if (!cr) {
        this.crCheck = { loading: false, warning: null, facilityId: null }
        this.form.erp_facility_id = null
        return
      }
      this.crCheckTimer = setTimeout(() => this.checkLeadCr(cr), 500)
    }
  },
  mounted() {
    this.fetchAccess()
    this.fetchStages()
    this.fetchTeam()
    this.fetchSchemes()
    this.fetchCampaigns()
    this.fetchLeads()
  },
  methods: {
    /** Karim, 18 Sep: campaign picker for the lead form — crm/campaigns?activeOnly=1. */
    async fetchCampaigns() {
      try {
        const res = await axios.get('crm/campaigns', { params: { activeOnly: 1 } })
        const body = decryptData(res.data.payload)
        this.campaignOptions = (body && body.data && body.data.campaigns) || []
      } catch (e) { /* the form still accepts free text if this fails */ }
    },
    /** Selecting a campaign fills BOTH campaign_id (real link) and the legacy
     * free-text campaign name (still read everywhere else, incl. required-field
     * validation) — see CrmLeadController::store()'s own backfill for symmetry. */
    onCampaignPicked(opt) {
      this.form.campaign_id = opt ? opt.id : null
      // taggable's own freshly-typed option only has `displayName` (the label key), no `.name`.
      this.form.campaign = opt ? (opt.name || opt.displayName || '') : ''
    },
    /** Karim, 2 Sep: show role + office next to each name when assigning. */
    memberLabel(member) {
      if (!member) return ''
      // Karim, 3 Sep: "should be markter name - office" — crm/team now
      // defaults to markter-only, so the role parenthetical was just noise
      // repeating "(markter)" on every row. Still shown for the rare ?all=1
      // case (a genuinely mixed-role list) where it's actually informative.
      const office = member.officeName || 'No office'
      const roles = Array.isArray(member.roles) ? member.roles : []
      const mixedRoles = roles.length && !(roles.length === 1 && roles[0] === 'markter')
      return mixedRoles ? `${member.name} (${roles.join('/')}) — ${office}` : `${member.name} — ${office}`
    },
    /**
     * BRD US 2.3, applied to lead intake: the moment a CR resolves to an
     * existing facility (in the CRM mirror, or in the ERP but not yet synced),
     * link the lead to it (erp_facility_id) so the opportunity this lead
     * becomes on convert()/assign(createOpportunity) carries company_id — the
     * only thing CrmAccess checks to give the assigned rep company visibility.
     * Without this the link was silently dropped (found running the manager-
     * assigns-lead loop, 2 Sep) and every new-lead company stayed invisible.
     */
    async checkLeadCr(cr) {
      this.crCheck.loading = true
      try {
        const res = await axios.get('crm/companies/check-cr', { params: { cr, deep: 1 }, timeout: 20000 })
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || null
        const facilityId = data?.match?.facilityId ?? data?.erp?.facilityId ?? null
        this.form.erp_facility_id = facilityId
        this.crCheck.facilityId = facilityId
        this.crCheck.warning = data?.warning || (data?.erp && !data?.match
          ? `CR ${cr} exists in the ERP as "${data.erp.name}" but has not been synced into the CRM yet — the lead will still link to it.`
          : null)
      } catch (e) {
        // Do not silently claim a link that was never verified.
        this.crCheck.warning = null
      } finally {
        this.crCheck.loading = false
      }
    },
    openMatchedFacility() {
      if (!this.crCheck.facilityId) return
      this.$router.push({ name: 'crm-system-company-show', params: { id: this.crCheck.facilityId } })
    },
    stageName(id) {
      const s = this.stages.find(x => x.id === id)
      return s ? s.name : id
    },
    statusStyle(id) {
      const s = this.stages.find(x => x.id === id)
      const color = s ? s.color : '#6e6b7b'
      return { color, backgroundColor: this.hexToSoft(color), borderColor: color }
    },
    hexToSoft(hex) {
      if (!hex || hex.charAt(0) !== '#') return 'rgba(110,107,123,.12)'
      const r = parseInt(hex.substr(1, 2), 16)
      const g = parseInt(hex.substr(3, 2), 16)
      const b = parseInt(hex.substr(5, 2), 16)
      return `rgba(${r},${g},${b},.13)`
    },
    isClosed(lead) {
      return lead.stage === 'won' || lead.stage === 'rejected'
    },
    rowClass(lead) {
      if (this.highlightId && String(lead.id) === this.highlightId) return 'lead-row--focus'
      if (lead.stage === 'won') return 'lead-row--won'
      if (lead.stage === 'rejected') return 'lead-row--lost'
      return ''
    },
    /** The last few completed activities, as a hover summary. */
    doneTooltip(lead) {
      const recent = (lead.doneActivities && lead.doneActivities.recent) || []
      if (!recent.length) return ''
      return recent
        .map(a => `${(a.date || '').slice(0, 10)} · ${a.type}${a.user ? ' (' + a.user + ')' : ''}${a.note ? ' — ' + a.note : ''}`)
        .join('\n')
    },

    isOverdue(date) {
      if (!date) return false
      return new Date(date) < new Date(new Date().toDateString())
    },
    /**
     * Karim, 4 Sep: "excel template for the sales people to add all the
     * current data of the current leads... so they upload each one current
     * work and continue on crm." Plain binary download — this endpoint does
     * not go through the AES-encrypted envelope every other CRM call uses
     * (Excel::download() bypasses it entirely), so no decryptData() here.
     */
    async downloadLeadTemplate() {
      this.downloadingTemplate = true
      try {
        const res = await axios.get('crm/leads/template', { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'crm-leads-template.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.notify('Failed', 'Could not download the template', 'danger', 'AlertCircleIcon')
      } finally {
        this.downloadingTemplate = false
      }
    },
    onImportFileChosen(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = '' // so choosing the same file twice still fires @change
      if (file) this.importLeadSheet(file)
    },
    async importLeadSheet(file) {
      this.importing = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await axios.post('crm/leads/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || {}
        this.importResults = data
        this.notify('Leads imported', `${data.created || 0} lead${data.created === 1 ? '' : 's'} added`)
        if (data.created) await this.fetchLeads()
      } catch (e) {
        const message = (e && e.response && e.response.data && e.response.data.message) || 'Could not read that file'
        this.notify('Import failed', message, 'danger', 'AlertCircleIcon')
      } finally {
        this.importing = false
      }
    },
    closeImportResults() {
      this.importResults = null
    },
    notify(title, text, variant = 'success', icon = 'CheckIcon') {
      this.$toast({ component: ToastificationContent, props: { title, text, variant, icon } })
    },

    async fetchStages() {
      try {
        const res = await axios.get('crm/leads/stages')
        const body = decryptData(res.data.payload)
        if (body && body.success && Array.isArray(body.data) && body.data.length) this.stages = body.data
      } catch (e) { /* defaults */ }
    },
    async fetchAccess() {
      try {
        const res = await axios.get('crm/me')
        const body = decryptData(res.data.payload)
        if (body && body.data) this.access = body.data
      } catch (e) { /* default: own only */ }
    },
    async fetchTeam() {
      try {
        const res = await axios.get('crm/team')
        const body = decryptData(res.data.payload)
        this.team = (body && body.data) || []
      } catch (e) { /* optional */ }
    },
    /**
     * Options for "Scope / service".
     * Source of truth is the PRODUCT catalog (crm_products.crm_name — the Odoo
     * scheme marketing actually sells, 270 rows loaded from the CSVs).
     * crm_services is only a fallback: it is the older catalog and may be empty,
     * which is exactly why this dropdown came up blank. Failures are no longer
     * swallowed silently — an empty list now says so in the UI.
     */
    async fetchSchemes() {
      this.schemeError = null

      try {
        const res = await axios.get('crm/products', { params: { group: 1 } })
        const body = decryptData(res.data.payload)
        const groups = body && body.data && body.data.groups
        if (Array.isArray(groups) && groups.length) {
          this.schemeOptions = groups.map(g => g.crmName).filter(Boolean).sort()
          return
        }
        const products = body && body.data && body.data.products
        if (Array.isArray(products) && products.length) {
          this.schemeOptions = [...new Set(products.map(p => p.crmName || p.crm_name).filter(Boolean))].sort()
          return
        }
      } catch (e) {
        console.warn('Product catalog unavailable, trying the service catalog:', e && e.message)
      }

      try {
        const res = await axios.get('crm/services')
        const body = decryptData(res.data.payload)
        const schemes = body && body.data && body.data.schemes
        if (Array.isArray(schemes) && schemes.length) {
          this.schemeOptions = schemes.map(s => s.scheme).filter(Boolean)
          return
        }
      } catch (e) {
        console.error('Service catalog failed too:', e)
      }

      this.schemeOptions = []
      this.schemeError = 'The product catalog did not load, so this list is empty. Run the CRM product seeder, then reopen this form.'
    },

    async fetchLeads() {
      this.loading = true
      try {
        const params = {}
        if (this.statusFilter) params.status = this.statusFilter
        if (this.assignedFilter) params.assigned = this.assignedFilter
        const res = await axios.get('crm/leads', { params })
        const body = decryptData(res.data.payload)
        this.leads = body && body.success && Array.isArray(body.data) ? body.data : []
        // Deep link from My Day: ?lead=LEAD-12 -> highlight that row
        const wanted = this.$route.query && this.$route.query.lead
        if (wanted) {
          this.searchQuery = ''
          this.highlightId = String(wanted)
        }
      } catch (e) {
        this.notify('Load failed', 'Could not load leads', 'danger', 'AlertCircleIcon')
      } finally {
        this.loading = false
      }
    },

    async fetchCustomers() {
      this.loadingCustomers = true
      try {
        const res = await axios.get('crm/dormant-customers', { params: { months: this.months } })
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || {}
        this.customers = data.customers || []
        this.customerCount = data.count || 0
      } catch (e) {
        this.notify('Load failed', 'Could not load customers', 'danger', 'AlertCircleIcon')
      } finally {
        this.loadingCustomers = false
      }
    },

    /**
     * One definition of a blank lead, used by data() and by openAdd().
     * is_broker starts as null, not false: BRD §2.2 wants the question answered,
     * and "unanswered" must stay distinguishable from "not a broker".
     */
    emptyForm() {
      return {
        id: null, company_name: '', contact_person: '', email: '', contact_number: '',
        scope: null, source: null, address: '', priority: 'Medium', responsible_id: null,
        // BRD §2.2 / §3.2 fields (ledger G-13)
        cr_number: '', is_broker: null, campaign: '', campaign_id: null, industry: '',
        job_position: '', website: '',
        // The facility this lead is for, once its CR resolves to one — see
        // checkLeadCr(). Carries through to the opportunity on convert/assign.
        erp_facility_id: null
      }
    },
    openAdd() {
      this.form = this.emptyForm()
      this.crCheck = { loading: false, warning: null, facilityId: null }
      this.showModal = true
    },
    /**
     * Karim, 7 Sep: "when the client is new I cannot create activity."
     * crm/leads/{id}/activity already existed on the backend (logs the
     * current next_activity as Done, sets the new one, optionally moves
     * stage) — this page just had no button that opened anything to call it.
     */
    openActivityModal(lead, emailMode = false) {
      this.activityLead = lead
      this.startInEmailMode = emailMode
      this.showActivityModal = true
    },
    closeActivityModal() {
      this.showActivityModal = false
      this.activityLead = null
      this.startInEmailMode = false
    },
    async submitLeadActivity({ outcomeNote, nextActivity }) {
      const lead = this.activityLead
      if (!lead) return
      try {
        await axios.post(`crm/leads/${lead.leadId}/activity`, { outcomeNote, nextActivity })
        this.notify('Activity saved', nextActivity ? `Next: ${nextActivity.type} on ${nextActivity.dueDate}` : 'Logged — nothing scheduled next')
        this.closeActivityModal()
        await this.fetchLeads()
      } catch (e) {
        this.notify('Failed', 'Could not save the activity', 'danger', 'AlertCircleIcon')
      }
    },
    /**
     * Karim, 7 Sep: "sales can be able to upload attachment with email send
     * ... email will be send on date selected there should be also a send
     * now option." Same crm/scheduled-emails endpoint PipelineCRM's cards use.
     */
    async submitLeadEmail({ to, cc, subject, body, sendAt, attachments }) {
      const lead = this.activityLead
      if (!lead) return

      const form = new FormData()
      form.append('contextType', 'lead')
      form.append('leadId', lead.leadId)
      to.forEach(addr => form.append('to[]', addr))
      cc.forEach(addr => form.append('cc[]', addr))
      form.append('subject', subject)
      form.append('body', body)
      if (sendAt) form.append('sendAt', sendAt)
      attachments.forEach(file => form.append('attachments[]', file))

      try {
        const res = await axios.post('crm/scheduled-emails', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const responseBody = decryptData(res.data.payload)
        const scheduled = responseBody && responseBody.data && responseBody.data.status === 'pending'
        this.notify(scheduled ? 'Email scheduled' : 'Email sent', scheduled ? `Will send on ${sendAt}` : `Sent to ${to.join(', ')}`, 'success', 'MailIcon')
        this.closeActivityModal()
        await this.fetchLeads()
      } catch (e) {
        this.notify('Not sent', (e.response && e.response.data && e.response.data.message) || 'The email could not be sent.', 'danger', 'AlertCircleIcon')
      }
    },
    editLead(lead) {
      this.form = {
        id: lead.leadId,
        company_name: lead.title,
        contact_person: lead.client,
        email: lead.email,
        contact_number: lead.phone,
        scope: lead.scope,
        source: lead.source,
        // `address` used to be hardcoded to '' here, so opening and saving a lead
        // silently erased its address. CrmLead::toFrontend() now returns it.
        address: lead.address || '',
        priority: lead.priority || 'Medium',
        responsible_id: lead.ownerId || null,
        // BRD §2.2 / §3.2 fields (ledger G-13) — read back from toFrontend()
        cr_number: lead.crNumber || '',
        is_broker: (lead.isBroker === null || lead.isBroker === undefined) ? null : !!lead.isBroker,
        campaign: lead.campaign || '',
        campaign_id: lead.campaignId || null,
        industry: lead.industry || '',
        job_position: lead.jobPosition || '',
        website: lead.website || '',
        erp_facility_id: lead.companyId || null
      }
      // Already linked (or not) as of the saved record — nothing to re-warn about
      // until the rep actually edits the CR number.
      this.crCheck = { loading: false, warning: null, facilityId: lead.companyId || null }
      this.showModal = true
    },

    async saveLead() {
      this.saving = true
      try {
        const payload = { ...this.form }
        // Send NULL, not '', for the optional BRD fields. The duplicate-CR work
        // (G-2) and every "how many leads have a campaign?" report test for NULL;
        // a table full of empty strings would read as "filled in".
        ;['cr_number', 'campaign', 'industry', 'job_position', 'website'].forEach(k => {
          if (typeof payload[k] === 'string' && !payload[k].trim()) payload[k] = null
        })
        const picked = this.team.find(u => u.id === payload.responsible_id)
        if (picked) payload.responsible_name = picked.name
        if (payload.id) {
          await axios.put(`crm/leads/${payload.id}`, payload)
          this.notify('Lead updated', payload.company_name)
        } else {
          await axios.post('crm/leads', payload)
          this.notify('Lead created', payload.company_name)
        }
        this.showModal = false
        await this.fetchLeads()
      } catch (e) {
        this.notify('Save failed', 'Could not save the lead', 'danger', 'AlertCircleIcon')
      } finally {
        this.saving = false
      }
    },

    isSelected(lead) {
      return this.selectedIds.includes(lead.leadId)
    },
    toggleSelect(lead) {
      const idx = this.selectedIds.indexOf(lead.leadId)
      if (idx === -1) this.selectedIds.push(lead.leadId)
      else this.selectedIds.splice(idx, 1)
    },
    toggleSelectAll() {
      if (this.allFilteredSelected) {
        const ids = new Set(this.filteredLeads.map(l => l.leadId))
        this.selectedIds = this.selectedIds.filter(id => !ids.has(id))
      } else {
        const have = new Set(this.selectedIds)
        this.filteredLeads.forEach(l => { if (!have.has(l.leadId)) this.selectedIds.push(l.leadId) })
      }
    },
    clearSelection() {
      this.selectedIds = []
      this.bulkAssignTo = ''
    },
    async bulkAssign() {
      if (!this.selectedIds.length || !this.bulkAssignTo) return
      const user = this.team.find(u => String(u.id) === String(this.bulkAssignTo))
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/leads/bulk-action', {
          action: 'assign',
          scope: 'ids',
          ids: this.selectedIds,
          responsibleId: user ? user.id : null,
          responsibleName: user ? user.name : null
        })
        const body = decryptData(res.data.payload)
        this.notify('Leads assigned', (body && body.message) || `Now with ${user ? user.name : 'nobody'}`)
        this.clearSelection()
        await this.fetchLeads()
      } catch (e) {
        this.notify('Failed', 'Could not assign the selected leads', 'danger', 'AlertCircleIcon')
      } finally {
        this.bulkBusy = false
      }
    },
    async bulkDelete() {
      if (!this.selectedIds.length) return
      const count = this.selectionCount
      if (!window.confirm(`Delete ${count} lead${count === 1 ? '' : 's'}? This cannot be undone.`)) return
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/leads/bulk-action', {
          action: 'delete',
          scope: 'ids',
          ids: this.selectedIds
        })
        const body = decryptData(res.data.payload)
        this.notify('Leads deleted', (body && body.message) || 'Done')
        this.clearSelection()
        await this.fetchLeads()
      } catch (e) {
        this.notify('Failed', 'Could not delete the selected leads', 'danger', 'AlertCircleIcon')
      } finally {
        this.bulkBusy = false
      }
    },

    /** Assign the lead to a salesperson — he then drives it from his pipeline. */
    async assignLead(lead, userId) {
      const user = this.team.find(u => String(u.id) === String(userId))
      try {
        await axios.post(`crm/leads/${lead.leadId}/assign`, {
          responsible_id: user ? user.id : null,
          responsible_name: user ? user.name : null
        })
        lead.ownerId = user ? user.id : null
        lead.owner = user ? user.name : null
        this.notify('Lead assigned', user ? `Now with ${user.name}` : 'Unassigned')
      } catch (e) {
        this.notify('Failed', 'Could not assign the lead', 'danger', 'AlertCircleIcon')
      }
    },

    async assignCustomer(c) {
      const userId = this.custAssign[c.clientId]
      const user = this.team.find(u => String(u.id) === String(userId))
      if (!user) return
      try {
        await axios.post(`crm/dormant-customers/${c.clientId}/assign`, {
          responsible_id: user.id,
          responsible_name: user.name
        })
        this.notify('Customer assigned', `${user.name} will call ${c.name}`)
        this.customers = this.customers.filter(x => x.clientId !== c.clientId)
        this.customerCount = Math.max(0, this.customerCount - 1)
      } catch (e) {
        this.notify('Failed', 'Could not assign the customer', 'danger', 'AlertCircleIcon')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.leads-crm { padding-bottom: 2rem; }

.lead-header {
  background: linear-gradient(135deg, #2563eb, #9e95f5);
  color: #fff; border-radius: .6rem; padding: 1.1rem 1.3rem .3rem; margin-bottom: 1rem;
  &__content { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; }
  &__icon { background: rgba(255,255,255,.2); border-radius: 50%; padding: .55rem; display: flex; }
  &__title { font-size: 1.4rem; font-weight: 700; margin: 0; color: #fff; }
  &__subtitle { margin: 0; opacity: .9; font-size: .85rem; }
  &__actions { display: flex; align-items: center; flex-wrap: wrap; }
}

.lead-tabs { display: flex; gap: .3rem; margin-top: .9rem; }
.lead-tab {
  border: 0; background: transparent; color: rgba(255,255,255,.8); cursor: pointer;
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .5rem .85rem; border-radius: .4rem .4rem 0 0; font-size: .85rem; font-weight: 600;
  &--active { background: #f8f8fb; color: #2563eb; }
  &__badge {
    background: rgba(255,255,255,.25); border-radius: 10px; padding: 0 .4rem; font-size: .7rem;
  }
  &--active .lead-tab__badge { background: rgba(37, 99, 235,.15); color: #2563eb; }
}

.lead-btn {
  display: inline-flex; align-items: center; gap: .35rem; border: 0; cursor: pointer;
  padding: .45rem .8rem; border-radius: .35rem; font-size: .82rem; font-weight: 600;
  background: #f1f1f4; color: #5e5873; transition: .15s;
  &:hover { filter: brightness(.96); }
  &--primary { background: #2563eb; color: #fff; }
  &--secondary { background: #ededf3; color: #5e5873; }
  &--success { background: #28c76f; color: #fff; }
  &--danger { background: #ea5455; color: #fff; }
  &--ghost { background: #fff; border: 1px solid #e4e4ec; color: #5e5873; }
  &--sm { padding: .25rem .5rem; font-size: .74rem; }
  &:disabled { opacity: .5; cursor: not-allowed; }
}

.lead-bulk-bar {
  display: flex; align-items: center; flex-wrap: wrap; gap: .3rem;
  background: rgba(37, 99, 235,.08); border: 1px solid rgba(37, 99, 235,.25);
  border-radius: .4rem; padding: .5rem .8rem; margin-bottom: 1rem;
  &__count { font-size: .82rem; font-weight: 700; color: #2563eb; }
}

.lead-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: .8rem; margin-bottom: 1rem; }
.lead-stat {
  background: #fff; border-radius: .6rem; padding: .85rem 1rem; display: flex; align-items: center; gap: .7rem;
  box-shadow: 0 2px 8px rgba(34,41,47,.08);
  &__icon { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
  &__value { font-size: 1.2rem; font-weight: 700; line-height: 1; }
  &__label { font-size: .76rem; color: #6e6b7b; }
  &--primary .lead-stat__icon { background: rgba(37, 99, 235,.14); color: #2563eb; }
  &--warning .lead-stat__icon { background: rgba(59, 130, 246,.14); color: #3b82f6; }
  &--success .lead-stat__icon { background: rgba(40,199,111,.14); color: #28c76f; }
  &--danger .lead-stat__icon { background: rgba(234,84,85,.14); color: #ea5455; }
}

.lead-toolbar { display: flex; align-items: center; flex-wrap: wrap; margin-bottom: 1rem; }
.lead-search {
  position: relative; flex: 1; min-width: 200px;
  &__icon { position: absolute; left: .6rem; top: 50%; transform: translateY(-50%); color: #b9b9c3; }
  &__input {
    width: 100%; padding: .5rem .7rem .5rem 2rem; border: 1px solid #e4e4ec; border-radius: .35rem; font-size: .85rem;
    &:focus { outline: none; border-color: #2563eb; }
  }
}
.lead-select {
  padding: .45rem .6rem; border: 1px solid #e4e4ec; border-radius: .35rem; font-size: .82rem; background: #fff; color: #5e5873;
  &--sm { padding: .2rem .35rem; font-size: .74rem; max-width: 125px; }
}

.lead-empty { text-align: center; padding: 3rem 1rem; background: #fff; border-radius: .6rem; }

.lead-table-wrap { background: #fff; border-radius: .6rem; overflow: auto; box-shadow: 0 2px 8px rgba(34,41,47,.08); }
.lead-table {
  width: 100%; border-collapse: collapse; font-size: .82rem;
  th {
    background: #f8f8fb; padding: .6rem .45rem; text-align: left; font-size: .7rem;
    text-transform: uppercase; color: #6e6b7b; white-space: nowrap; letter-spacing: .3px;
  }
  td { padding: .5rem .45rem; border-top: 1px solid #f0f0f5; vertical-align: middle; }
  tbody tr:hover { background: #fafaff; }
}

// Karim, 27 Sep: "the leads table is too wide, so you must scroll left... if
// the word is too much, by hovering the mouse it can show the rest." Long
// text is cut with "…" and every cut cell carries a :title with the full text.
.lead-trunc {
  display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: default;
  &--company { max-width: 150px; }
  &--email { max-width: 160px; }
  &--note { max-width: 150px; }
}
.lead-nowrap { white-space: nowrap; }
.lead-row--won { background: rgba(40,199,111,.05); }
.lead-row--focus { background: rgba(37, 99, 235,.12); box-shadow: inset 3px 0 0 #2563eb; }
.lead-row--lost { background: rgba(234,84,85,.04); opacity: .75; }

.lead-contact {
  a { display: block; font-size: .74rem; color: #2563eb; text-decoration: none; }
  a:hover { text-decoration: underline; }
}

// Karim, 8 Sep: "the width of the column width is big, must be smaller, and
// if anyone needs more details, he can hover it" — truncate, the `:title`
// tooltip already on this cell shows the full text on hover.
.lead-scope-cell {
  max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  cursor: default;
}

.lead-chip { background: #f1f1f6; border-radius: 10px; padding: .05rem .45rem; font-size: .68rem; color: #6e6b7b; }

.lead-status {
  display: inline-block; font-size: .7rem; font-weight: 700; padding: .12rem .5rem;
  border-radius: 10px; border: 1px solid; white-space: nowrap;
}
.lead-outcome {
  font-size: .66rem; font-weight: 700; margin-top: .15rem;
  &--won { color: #28c76f; }
  &--lost { color: #ea5455; }
}

.mr-05 { margin-right: .25rem; }

.lead-modal {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(34,41,47,.5); z-index: 1050;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
  &__box { background: #fff; border-radius: .6rem; width: 100%; max-width: 640px; max-height: 92vh; display: flex; flex-direction: column; }
  &__head { display: flex; justify-content: space-between; align-items: center; padding: .9rem 1.1rem; border-bottom: 1px solid #f0f0f5; }
  &__body { padding: 1rem 1.1rem; overflow-y: auto; }
  &__foot { display: flex; justify-content: flex-end; gap: .5rem; padding: .8rem 1.1rem; border-top: 1px solid #f0f0f5; }
  &__close { border: 0; background: transparent; cursor: pointer; color: #6e6b7b; }
}
.lead-modal__head h4 { margin: 0; font-size: 1rem; }

.lead-file-input { display: none; }

.lead-import-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .82rem;

  th {
    text-align: left; font-weight: 600; color: #b9b9c3;
    font-size: .72rem; text-transform: uppercase; letter-spacing: .03em;
    padding: .35rem .5rem; border-bottom: 1px solid #ebe9f1;
  }
  td { padding: .35rem .5rem; border-bottom: 1px solid #f6f6f9; color: #5e5873; }
  tr:last-child td { border-bottom: 0; }

  &__row--error td { background: rgba(234, 84, 85, .06); color: #ea5455; }
}
</style>
